import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Paper, TextField, Typography, CircularProgress,
  FormControlLabel, Checkbox, FormGroup, Divider,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { exportService } from '../services/api';

const FALLBACK_WAREHOUSES = [
  { key: 'raw', label: 'Сировина' },
  { key: 'dyes', label: 'Барвники' },
  { key: 'packaging', label: 'Упаковка' },
  { key: 'technical', label: 'Техматеріали' },
  { key: 'consumable', label: 'Розхідні матеріали' },
  { key: 'product', label: 'Вироби / напівфабрикати' },
];

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function ExportPage() {
  const [searchParams] = useSearchParams();
  const entityFromUrl = searchParams.get('entity') || '';
  const [entities, setEntities] = useState([]);
  const [stockWarehouses, setStockWarehouses] = useState(FALLBACK_WAREHOUSES);
  const [loading, setLoading] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState(() => (entityFromUrl ? [entityFromUrl] : []));
  const [selectedWarehouses, setSelectedWarehouses] = useState(() => FALLBACK_WAREHOUSES.map((w) => w.key));
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const selectedEntities = useMemo(
    () => entities.filter((e) => selectedKeys.includes(e.key)),
    [entities, selectedKeys],
  );
  const needsPeriod = selectedEntities.some((e) => e.supports_period);
  const needsWarehouses = selectedKeys.includes('stock_items');
  const allSelected = entities.length > 0 && selectedKeys.length === entities.length;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    exportService
      .entities()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data)
          ? data
          : (data?.entities || data?.results || []);
        const wh = Array.isArray(data?.stock_warehouses) && data.stock_warehouses.length
          ? data.stock_warehouses
          : FALLBACK_WAREHOUSES;
        setEntities(list);
        setStockWarehouses(wh);
        setSelectedWarehouses(wh.map((w) => w.key));
        setSelectedKeys((prev) => {
          if (entityFromUrl && list.some((e) => e.key === entityFromUrl)) {
            return [entityFromUrl];
          }
          if (prev.length) {
            const valid = prev.filter((k) => list.some((e) => e.key === k));
            if (valid.length) return valid;
          }
          return list.length ? [list[0].key] : [];
        });
      })
      .catch((e) => {
        console.error(e);
        alert('Помилка завантаження списку сутностей для експорту');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityFromUrl]);

  const toggleKey = (key) => {
    setSelectedKeys((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const toggleWarehouse = (key) => {
    setSelectedWarehouses((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const selectAll = () => setSelectedKeys(entities.map((e) => e.key));
  const clearAll = () => setSelectedKeys([]);

  const runExport = async () => {
    if (!selectedKeys.length) return;
    if (needsWarehouses && !selectedWarehouses.length) {
      alert('Оберіть хоча б один склад для експорту залишків');
      return;
    }
    setExporting(true);
    try {
      const params = {};
      if (needsPeriod) {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      if (needsWarehouses) {
        params.warehouses = selectedWarehouses.join(',');
      }
      const res = await exportService.runXlsx(selectedKeys, params);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const name = selectedKeys.length === 1 && !needsWarehouses
        ? `${selectedEntities[0]?.label || 'export'}_${todayIso()}.xlsx`
        : `export_${todayIso()}.xlsx`;
      downloadBlob(blob, name);
    } catch (e) {
      console.error(e);
      let msg = e.message || 'Помилка експорту';
      try {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || parsed.error || msg;
        } else {
          msg = e.response?.data?.detail || msg;
        }
      } catch (_) { /* keep */ }
      alert(msg);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 960 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Експорт
      </Typography>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Що вивантажити
          </Typography>
          <Button size="small" onClick={selectAll} disabled={allSelected}>Обрати все</Button>
          <Button size="small" onClick={clearAll} disabled={!selectedKeys.length}>Зняти все</Button>
          <Typography variant="caption" color="text.secondary">
            Обрано: {selectedKeys.length}
          </Typography>
        </Box>

        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 0.25,
            maxHeight: 360,
            overflow: 'auto',
            pr: 1,
            mb: 2,
          }}
        >
          {entities.map((e) => (
            <FormControlLabel
              key={e.key}
              control={
                <Checkbox
                  size="small"
                  checked={selectedKeys.includes(e.key)}
                  onChange={() => toggleKey(e.key)}
                />
              }
              label={
                <Typography variant="body2">
                  {e.label}
                  {e.supports_period ? (
                    <Typography component="span" variant="caption" color="text.secondary"> · період</Typography>
                  ) : null}
                </Typography>
              }
            />
          ))}
        </FormGroup>

        {needsWarehouses ? (
          <>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                Склади (для «Склад: залишки»)
              </Typography>
              <Button size="small" onClick={() => setSelectedWarehouses(stockWarehouses.map((w) => w.key))}>Усі</Button>
              <Button size="small" onClick={() => setSelectedWarehouses([])}>Зняти</Button>
            </Box>
            <FormGroup
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 0.25,
                mb: 2,
              }}
            >
              {stockWarehouses.map((w) => (
                <FormControlLabel
                  key={w.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedWarehouses.includes(w.key)}
                      onChange={() => toggleWarehouse(w.key)}
                    />
                  }
                  label={<Typography variant="body2">{w.label}</Typography>}
                />
              ))}
            </FormGroup>
          </>
        ) : null}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {needsPeriod ? (
            <>
              <TextField
                size="small"
                type="date"
                label="З"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="По"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          ) : null}

          <Button
            variant="contained"
            disabled={
              !selectedKeys.length
              || exporting
              || (needsWarehouses && !selectedWarehouses.length)
            }
            onClick={runExport}
          >
            {exporting
              ? 'Експортуємо…'
              : selectedKeys.length > 1
                ? `Експортувати (${selectedKeys.length})`
                : 'Експортувати'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Файл — Excel (*.xlsx). Кілька сутностей / складів = кілька аркушів.
          Період — лише для позицій з позначкою «період».
        </Typography>
      </Paper>
    </Box>
  );
}
